import React, { useState, useCallback } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { TagInput } from '../../common/TagInput';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { bulkAddTags } from '../../../api/contacts';

interface AddTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContacts: string[];
  onSuccess?: () => Promise<void>;
}

export const AddTagsModal: React.FC<AddTagsModalProps> = ({
  isOpen,
  onClose,
  selectedContacts,
  onSuccess,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();

  // Function to add new tags to the React Query cache
  const addTagsToCache = (newTags: string[]) => {
    // Get the current data from the cache
    const queryKey = ['uniqueContactTags', organization?.id];
    const currentTags = queryClient.getQueryData<string[]>(queryKey) || [];

    // Add new tags that don't already exist in the cache
    const tagsToAdd = newTags.filter((tag) => !currentTags.includes(tag));
    if (tagsToAdd.length === 0) return; // No new tags to add

    // Create updated tags array and sort alphabetically
    const updatedTags = [...currentTags, ...tagsToAdd].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );

    // Update the cache with the modified data
    queryClient.setQueryData(queryKey, updatedTags);
  };

  // Handle tag changes from TagInput component
  const handleTagsChange = useCallback((newTags: string[]) => {
    setTags(newTags);
  }, []);

  const handleSubmit = async () => {
    if (tags.length === 0) {
      showToast({
        message: 'Please add at least one tag',
        variant: 'error',
      });
      return;
    }

    if (!organization?.id) {
      showToast({ message: 'No organization selected', variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      // One server-side bulk call: merges against authoritative current tags
      // so unrelated tags (in Speculo AND in FUB after sync) are preserved.
      // Replaces the previous per-contact PUT loop that clobbered them.
      const result = await bulkAddTags(organization.id, selectedContacts, tags);

      addTagsToCache(tags);

      if (result.truncated) {
        showToast({
          message: `Tags added to ${result.updated_contacts} of ${selectedContacts.length} contacts — selection exceeded server limit.`,
          variant: 'error',
        });
      } else {
        showToast({
          message: `Tags added to ${result.updated_contacts} contact${result.updated_contacts === 1 ? '' : 's'}`,
          variant: 'success',
        });
      }

      await onSuccess?.();
      setTags([]);
      onClose();
    } catch (error) {
      console.error('Error adding tags:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to add tags',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>
        <Typography variant="h3" className="font-bold">
          Add Tags
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <div>
            <Typography variant="small" className="text-muted-foreground">
              Adding new tags to {selectedContacts.length} selected contact
              {selectedContacts.length > 1 ? 's' : ''} (existing tags will be preserved)
            </Typography>
          </div>

          <div className="space-y-4">
            <TagInput
              tags={tags}
              onTagsChange={handleTagsChange}
              placeholder="Type a tag and press Enter"
            />
            <Typography variant="small" className="text-muted-foreground">
              Press Enter to add a tag
            </Typography>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting}>
            Add Tags
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
